import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Pencil, Printer, X } from 'lucide-react'
import {
  addLineItem, cancelInvoice, getInvoice, removeLineItem, updateInvoice,
} from '../../api/invoices'
import { Button, ButtonLink } from '../../components/ui/Button'
import { Card, ErrorBox, SectionTitle } from '../../components/ui/Card'
import { useConfirm } from '../../components/ui/ConfirmModal'
import { useToast } from '../../components/ui/Toast'
import { useFetch } from '../../hooks/useFetch'
import {
  crumbLinkClass, infoLabelClass, infoValueClass,
  tbodyHoverClass, tdClass, thClass,
} from '../../lib/tableStyles'
import { textareaClass } from '../../components/ui/FormField'
import { NumberStepper } from '../../components/ui/NumberStepper'
import { formatCurrency } from '@/lib/currency'
import type { DiscountType, InvoiceStatus, LineItem } from '../../types/invoices'
import { STATUS_COLORS, STATUS_LABELS } from '../../types/invoices'

const lineInputClass =
  'py-[0.4rem] px-[0.6rem] border border-border-input rounded-md text-[0.875rem] outline-none box-border bg-surface text-text w-full'

function StatusBadge({ status }: { status: InvoiceStatus }) {
  const { color, bg } = STATUS_COLORS[status]
  return (
    <span
      className="inline-flex items-center px-3 py-[0.2rem] rounded-full text-[0.82rem] font-semibold"
      style={{ color, background: bg }}
    >
      {STATUS_LABELS[status]}
    </span>
  )
}

function InfoField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-[1.1rem]">
      <div className={infoLabelClass}>{label}</div>
      <div className={infoValueClass}>{children}</div>
    </div>
  )
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
}

export default function InvoiceDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const toast = useToast()
  const confirm = useConfirm()

  const { data: invoice, loading, error, refetch } = useFetch(
    () => getInvoice(Number(id)),
    [id],
  )

  // Line items state — kept in sync with server via refetch
  const [lineItems, setLineItems] = useState<LineItem[]>([])
  const [newDesc, setNewDesc]       = useState('')
  const [newQty, setNewQty]         = useState('1')
  const [newPrice, setNewPrice]     = useState('0.00')
  const [newTaxInclusive, setNewTaxInclusive] = useState(false)
  const [newDiscountType, setNewDiscountType] = useState<DiscountType | null>(null)
  const [newDiscountValue, setNewDiscountValue] = useState('')
  const [addingItem, setAddingItem] = useState(false)
  const [removingId, setRemovingId] = useState<number | null>(null)
  const [notesValue, setNotesValue] = useState<string>('')
  const [savingNotes, setSavingNotes] = useState(false)
  const [transitioning, setTransitioning] = useState(false)
  const [editingDate, setEditingDate] = useState(false)
  const [dateValue, setDateValue] = useState('')
  const [savingDate, setSavingDate] = useState(false)

  // Sync local lineItems + notes from fetched invoice
  const prevId = invoice?.id
  if (invoice && (lineItems.length === 0 || invoice.id !== prevId)) {
    if (JSON.stringify(invoice.line_items) !== JSON.stringify(lineItems)) {
      setLineItems(invoice.line_items)
    }
    if (notesValue === '' && invoice.notes) {
      setNotesValue(invoice.notes ?? '')
    }
  }

  const handleTransition = async (newStatus: InvoiceStatus) => {
    if (!invoice) return
    setTransitioning(true)
    try {
      await updateInvoice(invoice.id, { status: newStatus })
      toast.success(`Invoice marked as ${STATUS_LABELS[newStatus].toLowerCase()}.`)
      refetch()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update status.')
    } finally {
      setTransitioning(false)
    }
  }

  const handleCancel = async () => {
    if (!invoice) return
    const ok = await confirm({
      title: 'Cancel invoice?',
      message: `Invoice ${invoice.invoice_number} will be marked as cancelled. This cannot be undone.`,
      confirmLabel: 'Cancel Invoice',
    })
    if (!ok) return
    try {
      await cancelInvoice(invoice.id)
      toast.success('Invoice cancelled.')
      refetch()
    } catch {
      toast.error('Failed to cancel invoice.')
    }
  }

  const handleAddItem = async () => {
    if (!invoice || !newDesc.trim()) return
    setAddingItem(true)
    try {
      const item = await addLineItem(invoice.id, {
        description: newDesc.trim(),
        quantity: parseFloat(newQty) || 1,
        unit_price: parseFloat(newPrice) || 0,
        sort_order: lineItems.length,
        tax_inclusive: newTaxInclusive,
        discount_type: newDiscountType,
        discount_value: newDiscountType ? (parseFloat(newDiscountValue) || 0) : null,
      })
      setLineItems(prev => [...prev, item])
      setNewDesc('')
      setNewQty('1')
      setNewPrice('0.00')
      setNewTaxInclusive(false)
      setNewDiscountType(null)
      setNewDiscountValue('')
      refetch()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add line item.')
    } finally {
      setAddingItem(false)
    }
  }

  const handleRemoveItem = async (itemId: number) => {
    if (!invoice) return
    setRemovingId(itemId)
    try {
      await removeLineItem(invoice.id, itemId)
      setLineItems(prev => prev.filter(it => it.id !== itemId))
      refetch()
    } catch {
      toast.error('Failed to remove line item.')
    } finally {
      setRemovingId(null)
    }
  }

  const handleDateSave = async () => {
    if (!invoice || !dateValue || savingDate) return
    setSavingDate(true)
    try {
      await updateInvoice(invoice.id, { issued_date: dateValue })
      toast.success('Invoice date updated.')
      setEditingDate(false)
      refetch()
    } catch {
      toast.error('Failed to update date.')
    } finally {
      setSavingDate(false)
    }
  }

  const handleNotesSave = async () => {
    if (!invoice || savingNotes) return
    if (notesValue === (invoice.notes ?? '')) return
    setSavingNotes(true)
    try {
      await updateInvoice(invoice.id, { notes: notesValue || null })
      toast.success('Notes saved.')
      refetch()
    } catch {
      toast.error('Failed to save notes.')
    } finally {
      setSavingNotes(false)
    }
  }

  if (loading) return <div className="text-text-2 py-8">Loading…</div>

  if (error || !invoice) {
    return (
      <div>
        <ErrorBox>{error ?? 'Invoice not found.'}</ErrorBox>
        <Link to="/invoices" className={crumbLinkClass}>← Back to invoices</Link>
      </div>
    )
  }

  const isFinalised = invoice.status === 'cancelled' || invoice.status === 'paid'
  const newLineGross = (parseFloat(newQty) || 0) * (parseFloat(newPrice) || 0)
  const newDiscountAmount = newDiscountType
    ? (newDiscountType === 'percent'
        ? newLineGross * ((parseFloat(newDiscountValue) || 0) / 100)
        : (parseFloat(newDiscountValue) || 0))
    : 0
  const newLineTotal = Math.max(newLineGross - newDiscountAmount, 0).toFixed(2)

  return (
    <div>
      <nav className="text-[0.85rem] text-text-3 mb-5 flex items-center gap-[0.4rem]">
        <Link to="/invoices" className={crumbLinkClass}>Invoices</Link>
        <span>›</span>
        <span className="font-mono">{invoice.invoice_number}</span>
      </nav>

      {/* Header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="m-0 text-[1.5rem] font-bold text-text-heading font-mono">
            {invoice.invoice_number}
          </h1>
          <StatusBadge status={invoice.status} />
        </div>
        <div className="flex gap-2 flex-wrap">
          {invoice.status === 'draft' && (
            <Button variant="outline" onClick={async () => {
              if (lineItems.length === 0) {
                const ok = await confirm({ message: 'This invoice has no line items. Send anyway?', confirmLabel: 'Send' })
                if (!ok) return
              }
              void handleTransition('sent')
            }} disabled={transitioning}>
              Mark Sent
            </Button>
          )}
          {invoice.status === 'sent' && (
            <Button onClick={() => void handleTransition('paid')} disabled={transitioning}>
              Mark Paid
            </Button>
          )}
          {invoice.status === 'sent' && invoice.due_date && new Date(invoice.due_date) < new Date() && (
            <Button variant="outline" onClick={() => void handleTransition('overdue')} disabled={transitioning}>
              Mark Overdue
            </Button>
          )}
          {(invoice.status === 'draft' || invoice.status === 'sent' || invoice.status === 'overdue') && (
            <Button variant="danger" onClick={() => void handleCancel()} disabled={transitioning}>
              Cancel Invoice
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => window.open(`/invoices/${invoice.id}/print`, '_blank')}
          >
            <Printer size={14} strokeWidth={2} className="mr-1" /> Print PDF
          </Button>
          <Button variant="outline" onClick={() => void navigate(-1)}>← Back</Button>
        </div>
      </div>

      {/* Info grid */}
      <div className="grid grid-cols-2 gap-4 mb-4 max-sm:grid-cols-1">
        <Card>
          <SectionTitle>Client &amp; Patient</SectionTitle>
          <InfoField label="Client">
            {invoice.client_name ? (
              <Link to={`/clients/${invoice.client_id}`} className={crumbLinkClass}>{invoice.client_name}</Link>
            ) : `ID ${invoice.client_id}`}
          </InfoField>
          <InfoField label="Patient">
            {invoice.patient_name ? (
              <Link to={`/patients/${invoice.patient_id}`} className={crumbLinkClass}>{invoice.patient_name}</Link>
            ) : invoice.patient_id ? `ID ${invoice.patient_id}` : <span className="text-text-4">—</span>}
          </InfoField>
        </Card>

        <Card>
          <SectionTitle>Dates</SectionTitle>
          <div className="mb-[1.1rem]">
            <div className={infoLabelClass}>Issued</div>
            {editingDate ? (
              <div className="flex gap-2 items-center mt-[0.2rem]">
                <input
                  type="date"
                  value={dateValue}
                  onChange={e => setDateValue(e.target.value)}
                  disabled={savingDate}
                  className="py-[0.35rem] px-[0.5rem] border border-border-input rounded-md text-[0.875rem] bg-surface text-text outline-none"
                />
                <button
                  onClick={() => void handleDateSave()}
                  disabled={savingDate || !dateValue}
                  className="py-[0.35rem] px-3 bg-accent text-accent-contrast border-none rounded-md text-[0.82rem] cursor-pointer font-semibold disabled:opacity-60"
                >
                  {savingDate ? '…' : 'Save'}
                </button>
                <button
                  onClick={() => setEditingDate(false)}
                  disabled={savingDate}
                  className="py-[0.35rem] px-3 bg-transparent text-text-3 border border-border-input rounded-md text-[0.82rem] cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-[0.925rem] text-text">
                {formatDate(invoice.issued_date)}
                {!isFinalised && (
                  <button
                    onClick={() => { setDateValue(invoice.issued_date.slice(0, 10)); setEditingDate(true) }}
                    className="bg-transparent border-none cursor-pointer text-text-3 text-[0.85rem] py-[0.1rem] px-[0.3rem] rounded leading-none"
                    title="Edit date"
                  >
                    <Pencil size={13} strokeWidth={1.75} />
                  </button>
                )}
              </div>
            )}
          </div>
          <InfoField label="Due">
            {invoice.due_date ? (
              <span className={new Date(invoice.due_date) < new Date() && invoice.status !== 'paid' ? 'text-danger-text' : 'text-text'}>
                {formatDate(invoice.due_date)}
              </span>
            ) : <span className="text-text-4">—</span>}
          </InfoField>
          {invoice.paid_at && (
            <InfoField label="Paid on">{formatDate(invoice.paid_at)}</InfoField>
          )}
        </Card>
      </div>

      {/* Line items */}
      <Card className="overflow-hidden p-0 mb-4">
        <div className="px-5 pt-5">
          <SectionTitle>Line Items</SectionTitle>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className={thClass}>Description</th>
                <th className={`${thClass} text-right w-[70px]`}>Qty</th>
                <th className={`${thClass} text-right w-[110px]`}>Unit Price</th>
                <th className={`${thClass} text-center w-[70px]`}>Tax incl.</th>
                <th className={`${thClass} text-right w-[110px]`}>Total</th>
                {!isFinalised && <th className={`${thClass} w-9`}></th>}
              </tr>
            </thead>
            <tbody className={tbodyHoverClass}>
              {lineItems.length === 0 ? (
                <tr>
                  <td colSpan={isFinalised ? 5 : 6} className={`${tdClass} text-center text-text-4 italic`}>
                    No line items yet
                  </td>
                </tr>
              ) : (
                lineItems.map(item => {
                  const gross = parseFloat(item.quantity) * parseFloat(item.unit_price)
                  const hasDiscount = item.discount_type != null && parseFloat(item.line_total) < gross
                  return (
                  <tr key={item.id}>
                    <td className={tdClass}>{item.description}</td>
                    <td className={`${tdClass} text-right text-text-2`}>{parseFloat(item.quantity).toFixed(2)}</td>
                    <td className={`${tdClass} text-right text-text-2`}>{formatCurrency(item.unit_price)}</td>
                    <td className={`${tdClass} text-center text-text-2`}>{item.tax_inclusive ? '✓' : ''}</td>
                    <td className={`${tdClass} text-right font-semibold`}>
                      {hasDiscount && (
                        <span className="text-text-4 line-through font-normal mr-1">{formatCurrency(gross)}</span>
                      )}
                      {formatCurrency(item.line_total)}
                    </td>
                    {!isFinalised && (
                      <td className={`${tdClass} text-center`}>
                        <button
                          onClick={() => void handleRemoveItem(item.id)}
                          disabled={removingId === item.id}
                          className="bg-transparent border-none cursor-pointer text-danger-text leading-none p-[0.2rem]"
                          title="Remove"
                        >
                          {removingId === item.id ? '…' : <X size={14} strokeWidth={2} />}
                        </button>
                      </td>
                    )}
                  </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Add line item form */}
        {!isFinalised && (
          <div className="border-t border-dashed border-border px-5 py-3">
            <div className="grid gap-2 items-center" style={{ gridTemplateColumns: '1fr 104px 110px 110px 36px' }}>
              <input
                type="text"
                value={newDesc}
                onChange={e => setNewDesc(e.target.value)}
                placeholder="Description…"
                className={lineInputClass}
                disabled={addingItem}
                maxLength={500}
              />
              <NumberStepper
                value={newQty}
                onChange={setNewQty}
                min={0.01}
                step={1}
                disabled={addingItem}
              />
              <input
                type="number"
                value={newPrice}
                onChange={e => setNewPrice(e.target.value)}
                className={`${lineInputClass} text-right`}
                min={0}
                step={0.01}
                disabled={addingItem}
              />
              <div className="text-right text-sm font-semibold text-text-2 py-[0.4rem]">
                {formatCurrency(newLineTotal)}
              </div>
              <button
                onClick={() => void handleAddItem()}
                disabled={addingItem || !newDesc.trim()}
                className={`border-none rounded-md text-lg font-bold leading-none py-[0.45rem] px-[0.5rem] text-center ${newDesc.trim() ? 'bg-accent text-accent-contrast cursor-pointer' : 'bg-border-input text-text-3 cursor-default'}`}
                title="Add line item"
              >
                {addingItem ? '…' : '+'}
              </button>
            </div>
            <div className="flex items-center gap-3 mt-2 text-[0.82rem] text-text-2">
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newTaxInclusive}
                  onChange={e => setNewTaxInclusive(e.target.checked)}
                  disabled={addingItem}
                  className="cursor-pointer"
                />
                Tax inclusive
              </label>
              <span className="flex items-center gap-1">
                Discount
                <select
                  value={newDiscountType ?? ''}
                  onChange={e => setNewDiscountType((e.target.value || null) as DiscountType | null)}
                  className="py-[0.25rem] px-1 border border-border-input rounded text-[0.82rem] outline-none bg-surface text-text"
                  disabled={addingItem}
                >
                  <option value="">None</option>
                  <option value="percent">%</option>
                  <option value="amount">R</option>
                </select>
                {newDiscountType && (
                  <input
                    type="number"
                    value={newDiscountValue}
                    onChange={e => setNewDiscountValue(e.target.value)}
                    className="w-16 py-[0.25rem] px-1 border border-border-input rounded text-[0.82rem] outline-none text-right bg-surface text-text"
                    min={0} max={newDiscountType === 'percent' ? 100 : undefined} step={0.01}
                    disabled={addingItem}
                  />
                )}
              </span>
            </div>
          </div>
        )}
      </Card>

      {/* Totals */}
      <Card className="mb-4">
        <SectionTitle>Totals</SectionTitle>
        <div className="max-w-[280px] ml-auto text-[0.9rem]">
          <div className="flex justify-between text-text-2 mb-[0.4rem]">
            <span>Subtotal (before discount)</span>
            <span>{formatCurrency((parseFloat(invoice.subtotal) + parseFloat(invoice.discount_amount)).toFixed(2))}</span>
          </div>
          {parseFloat(invoice.discount_amount) > 0 && (
            <div className="flex justify-between text-text-2 mb-[0.4rem]">
              <span>Discount</span><span>-{formatCurrency(invoice.discount_amount)}</span>
            </div>
          )}
          <div className="flex justify-between text-text-2 mb-[0.4rem]">
            <span>Tax / VAT ({(parseFloat(invoice.tax_rate) * 100).toFixed(2)}%)</span>
            <span>{formatCurrency(invoice.tax_amount)}</span>
          </div>
          <div className="flex justify-between font-bold text-text text-[1.05rem] border-t border-border pt-2 mt-[0.3rem]">
            <span>Total</span><span>{formatCurrency(invoice.total_amount)}</span>
          </div>
        </div>
      </Card>

      {/* Notes */}
      <Card className="mb-4">
        <SectionTitle>Notes</SectionTitle>
        {isFinalised ? (
          <div className={`text-[0.925rem] whitespace-pre-wrap ${invoice.notes ? 'text-text' : 'text-text-4 italic'}`}>
            {invoice.notes ?? 'No notes'}
          </div>
        ) : (
          <textarea
            value={notesValue}
            onChange={e => setNotesValue(e.target.value)}
            onBlur={() => void handleNotesSave()}
            className={`${textareaClass()} min-h-[80px] ${savingNotes ? 'opacity-70' : ''}`}
            placeholder="Payment instructions, additional details…"
            disabled={savingNotes}
          />
        )}
      </Card>

      {/* Links to related records */}
      {(invoice.consultation_id || invoice.appointment_id) && (
        <Card>
          <SectionTitle>Related Records</SectionTitle>
          <div className="flex gap-3 flex-wrap">
            {invoice.consultation_id && (
              <ButtonLink to={`/consultations/${invoice.consultation_id}`} variant="outline" size="sm">
                View Consultation →
              </ButtonLink>
            )}
            {invoice.appointment_id && (
              <ButtonLink to={`/appointments/${invoice.appointment_id}`} variant="outline" size="sm">
                View Appointment →
              </ButtonLink>
            )}
          </div>
        </Card>
      )}
    </div>
  )
}
